import React, { useMemo, useRef, useState } from "react";
import ToolTipPortal from "components/ToolTipPortal/ToolTipPortal";
import CancelIcon from "@mui/icons-material/Cancel";
import { grey } from "@mui/material/colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import PaidPayment from "./PaidPayments";
import { FormatMoneyNumber } from "utils/numberUtils";
import { useSelector } from "react-redux";
import {
  selectState,
  setIndexForDeletionAccountPayable,
  setIndexForPaymentCreation,
  setNote,
  setPaymentCreationPopover,
  updateAccountPayable,
  updateAccountPayableTemporarily,
  updatePayablePlanTemporarily,
} from "../accountPayable.slice";
import { useAppDispatch } from "hooks/hooks";
import { useWindowedRows } from "hooks/useWindowedRows";
import {
  getAllPayablePlans,
  updatePartnerPlanPayable,
} from "../../Main/main.slice";
import { partnersService } from "Dashboard/scenes/Partners/services/partner.service";
import InfoTooltip from "components/Popup/InfoTooltip";
import DatePicker from "react-datepicker";
import ProjectSelect from "Dashboard/scenes/Projects/components/ProjectSelect";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import customStyles from "Dashboard/scenes/Partners/styles/SelectStyle";
import { styles as dropdownStyles } from "Dashboard/components/SelectStyle";
import { IAccountPayablesWithPayments, IUpdateAccountPayableRequestParams } from "Dashboard/scenes/Partners/domain";
import { toast } from "react-toastify";
import { FormatDateIso } from "utils/dateUtils";
import NumberInput from "Dashboard/components/NumberInput";
import { IProjectOption } from "Dashboard/domain/IProjectOption";
import VatSelect from "Dashboard/components/VatSelect";
import { debouncePerRow } from "utils/debounceUtil";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { PARTNER_MODULE_NAME } from "Dashboard/scenes/Partners/domain/PartnerModule";

interface IProps {
  projects: IProjectOption[];
};

// Per-row footprint in px: the `.partners-paying .left/.right ... .row` rule
// sets height 16px + margin-bottom 10px. Keep in sync with that CSS.
const ROW_HEIGHT = 26;

const TableBody = ({ projects }: IProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { accountPayablesWithPayments } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);

  const initialState = {
    isOpen: false,
    top: 0,
    left: 0,
  };

  const [noteTooltip, setNoteTooltip] = useState(initialState);

  const [selectedNote, setSelectedNote] = useState("");
  const [selectedPayableId, setSelectedPayableId] = useState<number>();

  const list = accountPayablesWithPayments ?? [];

  // Each account renders one main row plus one row per paid payment, so items
  // have variable height.
  const itemHeights = useMemo(
    () => list.map((item) => (1 + item.paidPayments.length) * ROW_HEIGHT),
    [list]
  );

  const { contentRef, startIndex, endIndex, topSpacer, bottomSpacer } =
    useWindowedRows({ count: list.length, itemHeights });

  const close = () => {
    setNoteTooltip(initialState);

    if (selectedNote !== "" && selectedPayableId) {
      partnersService
        .updateAccountPayable({
          accountPayableId: selectedPayableId,
          note: selectedNote,
        })
        .then(() => {
          dispatch(
            setNote({ payableId: selectedPayableId, note: selectedNote })
          );
        });
    }
  };

  const handleInfoIconClick = (
    note: string,
    payableId: number,
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    setSelectedNote(note);
    setSelectedPayableId(payableId);
    setNoteTooltip({
      isOpen: true,
      top: e.clientY,
      left: e.clientX,
    });
  };

  const handlePlanAccountPayable = (
    partnerId: number,
    accountPayableId: number,
    planned: boolean
  ) => {
    const data = {
      partnerId: partnerId,
      accountPayableId: accountPayableId,
      planned: planned,
    };
    dispatch(updatePartnerPlanPayable(data));
    dispatch(updatePayablePlanTemporarily(data));
    dispatch(getAllPayablePlans());
  };

  const onChangeBasicField = (data: IUpdateAccountPayableRequestParams) => {
    dispatch(updateAccountPayableTemporarily(data));
    updateFieldWithDelay(data);
  };

  const updateFieldWithDelay = useMemo(
    () => debouncePerRow(async (data: IUpdateAccountPayableRequestParams) => {
      try {
        await dispatch(updateAccountPayable(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 403) {
          toast.error("Neočekivana greška");
        } else if (status === 400) {
          toast.error("Neočekivana greška");
        }
      }
    }, 1000, "accountPayableId"),
    [dispatch]
  );

  const getInputField = (item: IAccountPayablesWithPayments) => {
    return (<NumberInput
      formatAs="money"
      onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
        if (parsedValue !== item.accountPayable.amountInvoiced) {
          onChangeBasicField({
            accountPayableId: item.accountPayable.payableId,
            amountTotal: parsedValue
          });
        }
      }}
      value={`${item.accountPayable.amountInvoiced}`}
      className={`${item.accountPayable.isAdvance ? 'center bg-safe' : 'center'}`}
      disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
    />);
  }

  return (
    <div ref={tableRef} className="table-body">
      <div className="g-grid g-content" ref={contentRef}>
        {topSpacer > 0 && <div style={{ height: topSpacer }} aria-hidden />}
        {list
          .slice(startIndex, endIndex)
          .map((item, offset) => {
            const index = startIndex + offset;
            return (
            <React.Fragment key={item.accountPayable.payableId}>
              <div className="row">
                <div className="col center">
                  {hasFullAccessTo(user, PARTNER_MODULE_NAME)
                    ? <InfoTooltip tooltip="Obriši zapis" classesProp="btn delete-btn delete-button" element="span">
                      <button
                        className="btn delete-btn delete-button"
                        onClick={() => {
                          dispatch(setIndexForDeletionAccountPayable(index));
                        }}
                      >
                        <CancelIcon
                          sx={{ color: grey[300] }}
                          fontSize="small"
                        />
                      </button>
                    </InfoTooltip>
                    : undefined}
                </div>
                <div className="col center">
                  <input type="checkbox"
                    checked={item.accountPayable.isXxx}
                    onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                      ? (e) => {
                        onChangeBasicField({
                          accountPayableId: item.accountPayable.payableId,
                          isXxx: e.target.checked
                        })
                      }
                      : undefined}
                    disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                  />
                </div>
                <div className="col center">
                  <input type="text" className="bg-item center light"
                    onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                      ? (e) => {
                        onChangeBasicField({
                          accountPayableId: item.accountPayable.payableId,
                          invoiceId: e.target.value
                        });
                      }
                      : undefined}
                    value={item.accountPayable.invoiceId}
                    disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                  />
                </div>
                <div className="col">
                  <div className="projSelectContainer col">
                    {item.project.active && hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                      ? <ProjectSelect
                        options={projects}
                        onChange={(value: number) => {
                          onChangeBasicField({
                            accountPayableId: item.accountPayable.payableId,
                            projectId: value
                          })
                        }}
                        defaultValue={item.project.projectId}
                        customStyles={customStyles}
                        scrollContainerRef={tableRef}
                      /> : <input type="text" style={{ fontSize: "10px" }}
                        className="bg-item-dark" value={item.project.name}
                        readOnly />}
                    <span
                      className="note-wrapper"
                      onClick={(e) =>
                        handleInfoIconClick(
                          item.accountPayable.note,
                          item.accountPayable.payableId,
                          e
                        )
                      }
                    >
                      <BsFillQuestionCircleFill className="question" />
                    </span>
                  </div>
                </div>
                <div className="col center">
                  <div>
                    <DatePicker
                      popperProps={{ strategy: "fixed" }}
                      className="date-picker bg-item light"
                      showMonthDropdown
                      showYearDropdown
                      onChange={(date) => {
                        if (date) {
                          onChangeBasicField({
                            accountPayableId: item.accountPayable.payableId,
                            invoiceDate: FormatDateIso(date)
                          });
                        }
                      }}
                      selected={item.accountPayable.invoiceDate
                        ? new Date(item.accountPayable.invoiceDate) : null}
                      dateFormat="dd.MM.yy"
                      disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                    />
                  </div>
                </div>
                <div className="col center">
                  <div>
                    <DatePicker
                      popperProps={{ strategy: "fixed" }}
                      className="date-picker bg-item light"
                      showMonthDropdown
                      showYearDropdown
                      onChange={(date) => {
                        if (date) {
                          onChangeBasicField({
                            accountPayableId: item.accountPayable.payableId,
                            dueDate: FormatDateIso(date)
                          });
                        }
                      }}
                      selected={item.accountPayable.dueDate
                        ? new Date(item.accountPayable.dueDate) : null}
                      dateFormat="dd.MM.yy"
                      disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                    />
                  </div>
                </div>
                {item.accountPayable.isAdvance
                  ? <InfoTooltip tooltip="Avans" classesProp="col center" element="div">
                    {getInputField(item)}
                  </InfoTooltip>
                  : <div className="col center">
                    {getInputField(item)}
                  </div>
                }
                <div className="col center">
                  <div className="bg-item center">
                    <span className="strong important">
                      {FormatMoneyNumber(
                        item.accountPayable.amountInvoiced -
                        item.accountPayable.amountPaid
                      )}
                    </span>
                  </div>
                </div>
                <div className="col center">
                  <div className="bg-item center">
                    <span className="strong">
                      {FormatMoneyNumber(item.accountPayable.amountPaid)}
                    </span>
                  </div>
                </div>
                <div className="col center" />
                <div className="col center">
                  <div className="bg-item vat center">
                    <VatSelect
                      customStyles={dropdownStyles.dropdownInTable}
                      value={item.accountPayable.vatRate}
                      onChange={(vatRate: number) => {
                        onChangeBasicField({
                          accountPayableId: item.accountPayable.payableId,
                          vatRate: vatRate
                        });
                      }}
                      isDisabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                    />
                  </div>
                </div>
                <div className="col center">
                  {item.accountPayable.amountInvoiced -
                    item.accountPayable.amountPaid !== 0
                    && hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                    && (
                      <InfoTooltip tooltip="Plan" classesProp="planCheckbox">
                        <input
                          type="checkbox"
                          checked={item.planned}
                          onChange={(event) =>
                            handlePlanAccountPayable(
                              item.accountPayable.partnerId,
                              item.accountPayable.payableId,
                              event.target.checked
                            )
                          }
                        />
                      </InfoTooltip>
                    )}
                </div>
                <div className="col center">
                  <InfoTooltip tooltip="Nova uplata" classesProp="btn plus-btn" element="span">
                    <button
                      className="btn plus-btn"
                      onClick={hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                        ? (e) => {
                          dispatch(setIndexForPaymentCreation(index));
                          dispatch(
                            setPaymentCreationPopover({
                              isOpen: true,
                              left: e.clientX,
                              top: e.clientY + 5,
                            })
                          );
                        }
                        : undefined}
                      disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                    >
                      <FontAwesomeIcon icon={faPlusCircle} className="icon" />
                    </button>
                  </InfoTooltip>
                </div>
              </div>
              <PaidPayment item={item} accountIndex={index} />
            </React.Fragment>
            );
          })}
        {bottomSpacer > 0 && <div style={{ height: bottomSpacer }} aria-hidden />}
      </div>
      <ToolTipPortal
        {...noteTooltip}
        onClose={close}
        text="Napomena"
        offset={{ top: -46, left: 10 }}
      >
        <textarea
          onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? (e) => setSelectedNote(e.target.value) : undefined}
          disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
        >
          {selectedNote}
        </textarea>
      </ToolTipPortal>
    </div>
  );
};

export default TableBody;
